import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Text, WizardStepRenderParams, useOnNext } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import SectionButton from 'modules/questionnaires/components/Section/SectionButton'
import { EventBox } from 'modules/trainings/components/Revamp/NewClassWizard/EventDataStep/EventBox'
import { EventPreview } from 'modules/trainings/components/Revamp/NewClassWizard/EventDataStep/EventPreview'
import {
  ClassWizardData,
  SessionData,
  deserializeToWizardEventData,
  serializeToWizardEventData
} from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'

export const EventDataStep = ({
  data,
  onDataChanged,
  featureFlag
}: WizardStepRenderParams<ClassWizardData> & { featureFlag: boolean }) => {
  const { id } = useParams()
  const { company } = useCurrentSession()
  //We use 'editing' to know if we are ...
  // - true   : creating new session
  // - number : editing that session in the array
  // - false  : not editing, only showing the list of sessions
  const [editing, setEditing] = useState<number | boolean>(false)

  const onSave = (session: SessionData) => {
    const events = data.trainingSessions

    const event = serializeToWizardEventData(session, company.id)
    if (editing === true) {
      // We are creating a new session, add to array
      events.push(event)
    } else if (editing !== false) {
      // We are editing a session, update it
      events.splice(editing, 1, event)
    }

    onDataChanged({ ...data, trainingSessions: events })
    setEditing(false)
  }
  const onDelete = (index: number) => {
    const events = data.trainingSessions
    if (!events) return

    events.splice(index, 1)
    onDataChanged({ ...data, trainingSessions: events })
  }
  const onDuplicate = (index: number) => {
    track(trackingEvents.training.session.duplicate)
    const events = data.trainingSessions
    if (!events) return

    const event = events[index]
    if (!event) return
    const eventCopyName = i18n.t(
      'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.session_name_duplicate',
      { name: event.name }
    )

    events.push({ ...event, name: eventCopyName })
    onDataChanged({ ...data, trainingSessions: events })
  }
  const onEdit = (index: number) => {
    const events = data.trainingSessions
    if (!events) return

    const event = events[index]
    if (!event) return

    setEditing(index)
  }
  const onClose = () => {
    setEditing(false)
  }

  const getSessionDataToEdit = (index: number) => {
    const events = data.trainingSessions
    if (!events) return

    const event = events[index]
    if (!event) return

    return deserializeToWizardEventData(event)
  }

  useOnNext(() => {
    track(trackingEvents.training.trainingGroupCreationWizard.goToSessionsNextStep)
  })

  return (
    <Box gap="s16">
      <Box gap="s16" paddingX="s16">
        <Box gap="s8">
          <Text size="400" weight="semibold" color="grey1000">
            {i18n.t('trainings.trainings_revamp.training_class.class_wizard.event_data_step.title')}
          </Text>
          <Text size="200" weight="normal">
            {i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.event_data_step.description'
            )}
          </Text>
        </Box>

        {editing !== false && (
          <EventBox
            trainingId={Number(id)}
            trainingClassId={0}
            initialData={typeof editing === 'number' ? getSessionDataToEdit(editing) : undefined}
            onClose={onClose}
            onSave={onSave}
            groupStart={data.startDate}
            groupEnd={data.endDate}
            featureFlag={featureFlag}
          />
        )}
        {editing === false &&
          data.trainingSessions.map((event, index) => (
            <EventPreview
              event={event}
              key={index}
              index={index}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onEdit={onEdit}
            />
          ))}
      </Box>

      {editing === false && (
        <Box paddingRight="s12" paddingLeft="s12">
          <SectionButton icon="add" onClick={() => setEditing(true)}>
            {i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.event_data_step.add_session'
            )}
          </SectionButton>
        </Box>
      )}
    </Box>
  )
}
